import { css } from '@emotion/react';
import { t } from 'i18n';
import { Fragment } from 'react';

import { theme } from '../theme';

const styles = {
  container: css`
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    width: 100%;
    position: relative;
  `,
  stop: css`
    font-size: ${theme.fontSize.sm};
    font-weight: ${theme.fontWeight.black};
    color: ${theme.colors.gray40};
    z-index: 2;
  `,
  raisedStopContainer: css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 2;
  `,
  raisedStop: css`
    color: ${theme.colors.gray40};
    font-size: ${theme.fontSize.xs};
    font-weight: ${theme.fontWeight.bold};
    position: relative;
    top: -10px;
  `,
  grayOverlay: css`
    position: absolute;
    width: 30px;
    height: 2px;
    background-color: ${theme.colors.gray80};
    top: 10px;

    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 2px;
      height: ${theme.spacing.sm};
      background-color: ${theme.colors.blue30};
      top: -3px;
    }
    &::before {
      left: -2px;
    }
    &::after {
      right: -2px;
    }
  `,
  lineContainer: css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    position: relative;
    width: 100%;
    margin: 0 8px 6px 8px;
  `,
  fullLine: (hasStops: boolean) => css`
    position: absolute;
    top: ${hasStops ? '10px' : '2px'};
    width: 100%;
    height: 2px;
    background-color: ${theme.colors.blue40};

    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 6px;
      height: 6px;
      background-color: ${theme.colors.blue30};
      border-radius: 50%;
      top: -2px;
    }
    &::before {
      left: -2px;
    }
    &::after {
      right: -2px;
  `,
  lineWrapper: css`
    display: flex;
    justify-content: space-evenly;
    width: 100%;
  `,
  operators: css`
    font-size: ${theme.fontSize.xs};
    color: ${theme.colors.gray40};
    margin-top: ${theme.spacing.xs};
  `,
};

type Props = {
  stops: string[];
  operators: string;
};

export const FlightStops = ({ stops, operators }: Props) => {
  if (!stops || stops.length < 2) return null;

  const origin = stops[0];
  const destination = stops[stops.length - 1];

  const intermediateStops = stops.slice(1, -1);
  const hasStops = intermediateStops.length > 0;

  return (
    <>
      <div css={styles.container}>
        <div css={styles.stop}>{origin}</div>

        <div css={styles.lineContainer}>
          <div css={styles.lineWrapper}>
            {intermediateStops.length > 0 ? (
              <>
                {intermediateStops.map(stop => (
                  <Fragment key={stop}>
                    <div css={styles.raisedStopContainer}>
                      <div css={styles.raisedStop}>{stop}</div>
                      <div css={styles.grayOverlay} />
                    </div>
                  </Fragment>
                ))}
              </>
            ) : null}
          </div>

          <div css={styles.fullLine(hasStops)} />
        </div>

        <div css={styles.stop}>{destination}</div>
      </div>

      <div css={styles.operators}>{t('operatedBy', { airline: operators })}</div>
    </>
  );
};
